import { get, orderBy, sortBy, union } from 'lodash'
import moment from 'moment'
import React from 'react'
import { getUserById, getLabel } from '../../../dux/modules/utils'
import { dateRangeMenuItems } from './menuConstants'
import { assetName } from '../../../dux/modules/utils'

/*
 * Returns the sum of an array of integers.
 * @param    numArray    <[int]>      An array of integers to sum
 */
const sumElements = (numArray) => {
  return numArray.reduce((total, element) => total + parseInt(element, 10), 0)
}

/*
 * Checks if a date is inside a given date range.
 * @param    date        <{moment}>              A moment date to test
 * @param    dateRange   <[{moment}, {moment}]>  An array describing the start and end
 *                                               date of the date range. (moment objects)
 */
const dateIsInRange = (date, dateRange) => {
  if (date.isSame(dateRange)) {
    return true
  }
  return date.isBetween(dateRange[0], dateRange[1], null, '[]')
}

/*
 * A helper function used to convert an "activeDurationKey" and
 * "customDateRange" combination into a valid array of two moment
 * objects. The activeDurationKey will determine the key in the
 * dateRangeMenuItems collection to look up so the function can compute
 * a date range.
 * If the active duration key is set to custom, then the function will use
 * the customDateRange for the return value.
 *
 * @param    activeDurationKey   <string>            The key of the selected activeDuration to
 *                                                   lookup the correct dateRangeMenuItem.
 * @param    customDateRange     <[string, string]>  The date strings to use for the date range
 *                                                   calculation.
 */
export const generateDateRange = (
  activeDurationKey,
  customDateRange,
  startDate = '2019-10-01',
) => {
  // if "Today" is selected, the range should be start of day til now
  if (activeDurationKey === '1d') {
    const now = moment()
    return [now.startOf('day'), now]
  }

  const activeDurationObj = dateRangeMenuItems.find(
    (menuItem) => menuItem.key === activeDurationKey,
  )
  const activeDuration = activeDurationObj && activeDurationObj.duration
  const activeDurationUnit = activeDurationObj && activeDurationObj.unit

  const hardStartDate = moment(startDate)
  const customStartDate = moment(customDateRange[0])
  const durationStartDate = moment().subtract(
    activeDuration,
    activeDurationUnit,
  )

  const momentStart =
    activeDurationKey === 'custom' && customStartDate.isAfter(hardStartDate)
      ? customStartDate
      : activeDurationKey !== 'all' && durationStartDate.isAfter(hardStartDate)
        ? durationStartDate
        : hardStartDate

  const momentEnd =
    activeDurationKey === 'custom' ? moment(customDateRange[1]) : moment()

  return [momentStart.startOf('day'), momentEnd.endOf('day')]
}

/*
 * Massages the analytics api data (which comes from the redux store) so that
 * it can be displayed in the BAM Spark Charts.
 * @param    dataObj     <object>            The chart data. As formatted in the redux store.
 * @param    chartType   <string>            The type of spark chart where the data is to be used.
 * @param    bamPlatform <string>            Is "dam" or "mst"
 * @param    dateRange   <[string, string]>  The date range to filter the data for.
 */
export const formatDataForSparkChart = ({
  dataObj,
  chartType,
  bamPlatform,
  dateRange,
}) => {
  const dataByDate = get(dataObj, 'data', null)

  if (!dataByDate) return null

  let numList = []
  //let prevDateMoment = null
  for (let date in dataByDate) {
    const dateMoment = moment(date)
    // Date in Date Range Filter
    if (!dateIsInRange(dateMoment, dateRange)) continue

    // Concat
    switch (chartType) {
      case 'users':
        bamPlatform !== 'all'
          ? numList.push(get(dataByDate[date], `${bamPlatform}ActiveUsers`, 0))
          : numList.push(
              get(dataByDate[date], 'damActiveUsers', 0) +
                get(dataByDate[date], 'mstActiveUsers', 0),
            )
        break
      case 'assetsViewed':
        bamPlatform !== 'all'
          ? numList.push(get(dataByDate[date], `${bamPlatform}AssetsViewed`, 0))
          : numList.push(
              get(dataByDate[date], 'damAssetsViewed', 0) +
                get(dataByDate[date], 'mstAssetsViewed', 0),
            )
        break
      case 'assetsShared':
        bamPlatform !== 'all'
          ? numList.push(get(dataByDate[date], `${bamPlatform}AssetsShared`, 0))
          : numList.push(
              get(dataByDate[date], 'damAssetsShared', 0) +
                get(dataByDate[date], 'mstAssetsShared', 0),
            )
        break
      case 'calcsViewed':
        bamPlatform !== 'all'
          ? numList.push(get(dataByDate[date], `${bamPlatform}CalcsViewed`, 0))
          : numList.push(
              get(dataByDate[date], 'damCalcsViewed', 0) +
                get(dataByDate[date], 'mstCalcsViewed', 0),
            )
        break
      case 'calculations':
        bamPlatform !== 'all'
          ? numList.push(get(dataByDate[date], `${bamPlatform}Calculations`, 0))
          : numList.push(
              get(dataByDate[date], 'damCalculations', 0) +
                get(dataByDate[date], 'mstCalculations', 0),
            )
        break
      case 'formSubmissions':
        bamPlatform !== 'all'
          ? numList.push(
              get(dataByDate[date], `${bamPlatform}FormSubmissions`, 0),
            )
          : numList.push(
              get(dataByDate[date], 'damFormSubmissions', 0) +
                get(dataByDate[date], 'mstFormSubmissions', 0),
            )
        break
      case 'quizSubmissions':
        bamPlatform !== 'all'
          ? numList.push(
              get(dataByDate[date], `${bamPlatform}QuizSubmissions`, 0),
            )
          : numList.push(
              get(dataByDate[date], 'damQuizSubmissions', 0) +
                get(dataByDate[date], 'mstQuizSubmissions', 0),
            )
        break
      case 'totalEvents':
        bamPlatform !== 'all'
          ? numList.push(get(dataByDate[date], `${bamPlatform}TotalEvents`, 0))
          : numList.push(
              get(dataByDate[date], 'damTotalEvents', 0) +
                get(dataByDate[date], 'mstTotalEvents', 0),
            )
        break
      default:
        console.warn('Unrecognized chartType', chartType)
        break
    }
  }

  return {
    total: sumElements(numList),
    dataList: numList,
  }
}

/*
 * Massages the analytics api data (which comes from the redux store) so that
 * it can be displayed in the BAM Temporal Line Graph.
 * @param    dataObj     <object>            The graph data. As formatted in the redux store.
 * @param    chartType   <string>            The type of temporal line graph where the data is
 *                                              to be used.
 * @param    bamPlatform <string>            Is "dam" or "mst"
 * @param    dateRange   <[string, string]>  The date range to filter the data for.
 * @param    selectedUserFilter <string>     User ID to restrict dataset to
 */
export const formatDataForLineGraph = ({
  dataObj,
  chartType,
  bamPlatform,
  dateRange,
  selectedUserFilter,
  groupUserIds,
}) => {
  if (chartType === 'users') {
    const dataByDate = get(dataObj, 'data', null)

    if (!dataByDate) {
      return {
        dataList: [],
        max: undefined,
      }
    }

    let numUsersCollection = []
    let maxNumUsers = 0
    for (let date in dataByDate) {
      const dateMoment = moment(date)
      // Date in Date Range Filter
      if (!dateIsInRange(dateMoment, dateRange)) continue

      let activeUsers = get(dataByDate[date], `${bamPlatform}ActiveUsers`, [])
      if (bamPlatform === 'all') {
        activeUsers = union(
          get(dataByDate[date], 'damActiveUsers', []),
          get(dataByDate[date], 'mstActiveUsers', []),
        )
      }

      if (selectedUserFilter) {
        activeUsers = activeUsers.filter((id) => id === selectedUserFilter)
      }

      if (groupUserIds) {
        activeUsers = activeUsers.filter((id) => groupUserIds.has(id))
      }

      const numUsers = activeUsers.length

      numUsersCollection.push({
        x: dateMoment.valueOf(),
        y: numUsers,
      })
      maxNumUsers = Math.max(numUsers, maxNumUsers)
    }

    return {
      dataList: numUsersCollection,
      max: maxNumUsers,
    }
  }
}

/**
 * Enumerate the set of dates between start and end (not inclusive)
 *
 * @param {moment.Moment} start the start date
 * @param {moment.Moment} end the end date
 * @returns {moment.Moment[]} an array of dates between the given start and end dates
 */
// inspired by https://stackoverflow.com/a/23796069
const enumerateDatesBetween = (start, end) => {
  const startDate = moment(start).startOf('day')
  const endDate = moment(end).startOf('day')
  const enumeratedDates = []

  while (startDate.add(1, 'day').diff(endDate) < 0) {
    enumeratedDates.push(startDate.clone())
  }

  return enumeratedDates
}

/**
 * @typedef {Object} AnalyticsPlatformData
 * @property {Object} dam - dated analytics event data object for DAM
 * @property {Object} mst - dated analytics event data object for MST
 *
 * @param {AnalyticsPlatformData} analyticsData
 */
function aggregatePlatformEvents(analyticsData) {
  if (!analyticsData) {
    throw new Error('analyticsData is required')
  }
  const { dam, mst } = analyticsData
  const aggregatedData = { ...(dam || {}) }
  if (mst) {
    Object.keys(mst).forEach((date) => {
      if (!aggregatedData[date]) {
        aggregatedData[date] = mst[date]
      } else {
        aggregatedData[date] = aggregatedData[date].concat(mst[date])
      }
    })
  }
  return aggregatedData
}

/*
 * Massages the analytics api data (which comes from the redux store) so that
 * it can be displayed in the BAM Bar Graph.
 * @param    dataObj       <object>            The graph data. As formatted in the redux store.
 * @param    bamPlatform   <string>            Is "dam" or "mst"
 * @param    dateRange     <[string, string]>  The date range to filter the data for.
 * @param    selectedAsset <string>            The selected asset id
 */
export const formatDataForBarGraph = ({
  dataObj,
  bamPlatform,
  dateRange,
  selectedAsset,
  selectedCalculator,
  selectedForm,
  allForms,
  cellFilters,
  allUsers,
  formAnalyticsSettings,
  skipIdFilter,
}) => {
  const dataByPlatform = get(dataObj, 'data', null)
  let dataByDate = get(dataByPlatform, `${bamPlatform}`, {})

  if (bamPlatform === 'all') {
    dataByDate = aggregatePlatformEvents(dataByPlatform)
  }

  if (Object.keys(dataByDate).length === 0) return null

  // Get form config for field mapping if a form is selected
  const formConfig =
    selectedForm && formAnalyticsSettings
      ? formAnalyticsSettings[selectedForm] || {}
      : {}
  const tableColumns = formConfig.tableColumns || []

  // Build a map from label -> field for reverse lookup
  const labelToField = {}
  for (const colConfig of tableColumns) {
    const fieldName =
      typeof colConfig === 'string' ? colConfig : colConfig.field
    const label =
      typeof colConfig === 'string'
        ? colConfig
        : colConfig.label || colConfig.field
    labelToField[label] = fieldName
  }

  // Helper to check if an event matches cell filters
  const eventMatchesCellFilters = (event) => {
    if (!cellFilters || Object.keys(cellFilters).length === 0) return true

    return Object.entries(cellFilters).every(([key, filterValue]) => {
      // Get the display value for comparison - handle React elements with array children
      let filterText = filterValue
      if (
        typeof filterValue === 'object' &&
        filterValue !== null &&
        filterValue.props
      ) {
        const children = filterValue.props.children
        if (Array.isArray(children)) {
          filterText = children
            .map((c) => (typeof c === 'string' ? c : ''))
            .join('')
        } else if (typeof children === 'string') {
          filterText = children
        } else {
          filterText = String(filterValue)
        }
      }

      // Look up the field name from the label
      const fieldName = labelToField[key] || key.toLowerCase()

      // Handle special system fields
      if (fieldName === '_user' || fieldName === 'user') {
        // User column contains "Name (email)" format
        const user = allUsers && getUserById(event.userId, allUsers)
        if (!user) return false
        const userName = get(user, 'name', 'unknown')
        const userEmail = get(user, 'email', 'unknown')
        const userDisplay = userName + ' (' + userEmail + ')'
        return userDisplay === filterText
      }

      if (fieldName === '_name' || fieldName === '_user_name') {
        const user = allUsers && getUserById(event.userId, allUsers)
        return user && user.name === filterText
      }

      if (fieldName === '_email' || fieldName === '_user_email') {
        const user = allUsers && getUserById(event.userId, allUsers)
        return user && user.email === filterText
      }

      if (fieldName === '_date') {
        const eventDate = moment(event.eventTime).format('YYYY-MM-DD')
        return eventDate === filterText
      }

      // For custom form fields, use findFormFieldValue
      const fieldValue = findFormFieldValue(event, fieldName)
      if (fieldValue !== undefined) {
        return String(fieldValue) === String(filterText)
      }

      // Fallback: check direct match on event properties
      const eventValue = event[key]
      if (eventValue === undefined) return false
      return String(eventValue) === String(filterText)
    })
  }

  let numViewsCollection = []

  for (let date in dataByDate) {
    if (!dataByDate.hasOwnProperty(date)) continue

    const dateMoment = moment(date)

    if (
      !dateIsInRange(dateMoment, dateRange) ||
      dataByDate[date].length === 0
    ) {
      continue
    }

    numViewsCollection.push({
      x: dateMoment.format('YYYY-MM-DD'),
      y: dataByDate[date].filter(function (event) {
        var effectiveAssetId =
          skipIdFilter && !event.assetId ? 'unknown' : event.assetId
        return (
          (skipIdFilter || event.assetId || event.calcId || event.formId) &&
          ((!selectedAsset && !selectedCalculator && !selectedForm) ||
            (selectedAsset && selectedAsset === effectiveAssetId) ||
            (selectedCalculator && selectedCalculator === event.calcId) ||
            (selectedForm && selectedForm === event.formId)) &&
          eventMatchesCellFilters(event)
        )
      }).length,
    })
  }
  // sort collection so that last event date is accurate
  numViewsCollection = sortBy(numViewsCollection, 'x')

  if (numViewsCollection.length > 0) {
    // transform the collection by filling in all dates between those w/data with zero-value
    // entries
    numViewsCollection = numViewsCollection.reduce((acc, item, idx, array) => {
      let prevDate
      const nextDate = moment(item.x).startOf('day')

      if (idx === 0) {
        // start from one day before the date range, to include the initial range in enumeration
        prevDate = moment(dateRange[0]).subtract(1, 'day').startOf('day')
      } else {
        prevDate = moment(array[idx - 1].x).startOf('day')
      }

      return acc.concat(
        enumerateDatesBetween(prevDate, nextDate).map((item) => ({
          x: item.format('YYYY-MM-DD'),
          y: 0,
        })),
        item,
      )
    }, [])

    const lastEventDate = moment(
      numViewsCollection[numViewsCollection.length - 1].x,
    ).startOf('day')
    const endDate = moment(dateRange[1]).add(1, 'day').startOf('day')

    numViewsCollection.push(
      ...enumerateDatesBetween(lastEventDate, endDate).map((item) => ({
        x: item.format('YYYY-MM-DD'),
        y: 0,
      })),
    )
  }

  return sortBy(numViewsCollection, 'x')
}

/*
 * Massages the analytics api data (which comes from the redux store) so that
 * it can be displayed in the BAM Analytics User Table.
 * @param    activeDate   <string>            The active date to select the data for.
 * @param    allUsers     <object>            The dictionary to use for user data to display.
 * @param    bamPlatform  <string>            Is "dam" or "mst"
 * @param    dataObj      <object>            The chart data. As formatted in the redux store.
 * @param    dateRange    <[string, string]>  The date range to filter the data for
 * @param    selectedUser <object>            The selected user filter
 */
export const formatDataForUserTable = ({
  allUsers,
  activeDate,
  bamPlatform,
  dataObj,
  dateRange,
  selectedUser,
  showInactive,
  showDeleted,
  groupUserIds,
}) => {
  const data = get(dataObj, 'data', {})

  // If a specific date is selected, show users active on that date
  if (activeDate) {
    const collectedUsers = []
    const dateStr = moment(activeDate).format('YYYY-MM-DD')
    const usersForDate =
      bamPlatform === 'all'
        ? union(
            get(data[dateStr], 'damActiveUsers', []),
            get(data[dateStr], 'mstActiveUsers', []),
          )
        : get(data[dateStr], `${bamPlatform}ActiveUsers`, [])

    usersForDate.forEach((userId) => {
      if (groupUserIds && !groupUserIds.has(userId)) return
      if (selectedUser && selectedUser.id !== userId) {
        return
      }

      const userData = getUserById(userId, allUsers)
      const name = get(userData, 'name', 'unknown')
      const email = get(userData, 'email', 'unknown')
      collectedUsers.push({
        key: userId,
        user: `${name} (${email})`,
      })
    })

    return sortBy(collectedUsers, ['user'])
  }

  // If a specific user is selected, show dates they were active
  if (selectedUser) {
    const activeDates = []

    for (let date in data) {
      if (!data.hasOwnProperty(date)) continue

      const dateMoment = moment(date)

      if (!dateIsInRange(dateMoment, dateRange)) continue

      const dateData = data[dateMoment.format('YYYY-MM-DD')]
      let usersWithEvents = get(dateData, `${bamPlatform}ActiveUsers`, [])
      if (bamPlatform === 'all') {
        usersWithEvents = union(
          get(dateData, 'damActiveUsers', []),
          get(dateData, 'mstActiveUsers', []),
        )
      }

      if (usersWithEvents.includes(selectedUser.id)) {
        activeDates.push({
          key: date,
          date: dateMoment.format('MMM D, YYYY'),
          sortDate: date,
        })
      }
    }

    return orderBy(activeDates, ['sortDate'], ['desc'])
  }

  // Default: show all users with their days active count
  const collectedUsers = []
  const userEvents = {}

  for (let date in data) {
    if (!data.hasOwnProperty(date)) continue

    const dateMoment = moment(date)

    if (!dateIsInRange(dateMoment, dateRange)) continue

    const dateData = data[dateMoment.format('YYYY-MM-DD')]
    let usersWithEvents = get(dateData, `${bamPlatform}ActiveUsers`, [])
    if (bamPlatform === 'all') {
      usersWithEvents = union(
        get(dateData, 'damActiveUsers', []),
        get(dateData, 'mstActiveUsers', []),
      )
    }
    usersWithEvents.forEach(
      (userId) => (userEvents[userId] = 1 + (userEvents[userId] || 0)),
    )
  }

  Object.values(allUsers).forEach((user) => {
    if (groupUserIds && !groupUserIds.has(user.id)) return
    if (user.deleted) {
      if (!showDeleted || !userEvents[user.id]) return
    } else {
      if (!showInactive && !userEvents[user.id]) return
    }
    collectedUsers.push({
      ...formatUserForDisplay(user),
      daysActive: userEvents[user.id] || 0,
      key: user.id,
    })
  })

  return orderBy(collectedUsers, ['daysActive', 'user'], ['desc', 'asc'])
}

const buildGroupUserEvents = ({
  allUsers,
  allGroups,
  bamPlatform,
  dataObj,
  dateRange,
}) => {
  const data = get(dataObj, 'data', {})
  const userEvents = {}
  for (let date in data) {
    if (!data.hasOwnProperty(date)) continue
    const dateMoment = moment(date)
    if (!dateIsInRange(dateMoment, dateRange)) continue
    const dateData = data[dateMoment.format('YYYY-MM-DD')]
    let usersWithEvents = get(dateData, `${bamPlatform}ActiveUsers`, [])
    if (bamPlatform === 'all') {
      usersWithEvents = union(
        get(dateData, 'damActiveUsers', []),
        get(dateData, 'mstActiveUsers', []),
      )
    }
    usersWithEvents.forEach(
      (userId) => (userEvents[userId] = 1 + (userEvents[userId] || 0)),
    )
  }
  const usersByEmail = {}
  Object.values(allUsers).forEach((user) => {
    if (user.email) usersByEmail[user.email] = user
  })
  return { userEvents, usersByEmail }
}

export const formatDataForGroupTable = ({
  allUsers,
  allGroups,
  bamPlatform,
  dataObj,
  dateRange,
  showSystemGroups,
}) => {
  if (!allGroups) return []

  const { userEvents, usersByEmail } = buildGroupUserEvents({
    allUsers,
    allGroups,
    bamPlatform,
    dataObj,
    dateRange,
  })

  const totalDays = Object.values(userEvents).reduce((sum, n) => sum + n, 0)

  const groupRows = []
  Object.keys(allGroups)
    .filter((groupName) => showSystemGroups || !groupName.startsWith('SYS.'))
    .forEach((groupName) => {
      const group = allGroups[groupName]
      let totalGroupDays = 0
      ;(group.members || []).forEach((email) => {
        const user = usersByEmail[email]
        if (user) totalGroupDays += userEvents[user.id] || 0
      })
      groupRows.push({
        key: groupName,
        group: group.name || groupName,
        daysActive: totalGroupDays,
      })
    })

  return [
    { key: '__all__', group: 'All Groups', daysActive: totalDays },
    ...orderBy(groupRows, ['daysActive', 'group'], ['desc', 'asc']),
  ]
}

function formatUserForDisplay(user) {
  const style = {}

  if (!user || user.deleted) {
    style.color = 'lightgray'
    style.textDecoration = 'line-through'
  }

  const name = get(user, 'name', 'unknown')
  const email = get(user, 'email', 'unknown')

  return {
    user: (
      <span style={style}>
        {name} ({email})
      </span>
    ),
    userId: get(user, 'id', ''),
  }
}

export const gatherUserDataForExport = ({
  // gather all events as an array of table objects
  // primary data:
  dataObj,
  eventType, // "asset views" or "asset shares" or "user days active"
  // context data:
  allUsers, // Map of all users
  // filters:
  bamPlatform, // Is "dam" or "mst"
  selectedUser,
  dateRange, // The date range to filter the data for.
  groupUserIds, // Optional Set<userId> to restrict to group members
  selectedGroupName, // Optional group name for export filename
}) => {
  // gather events for specified platform(s)
  const eventsByDate = {}
  Object.keys(dataObj.data).forEach((date) => {
    const dateData = dataObj.data[date]
    const activeUsers =
      bamPlatform === 'dam'
        ? dateData['damActiveUsers']
        : bamPlatform === 'mst'
          ? dateData['mstActiveUsers']
          : union(dateData['damActiveUsers'], dateData['mstActiveUsers'])
    if (activeUsers && activeUsers.length > 0) eventsByDate[date] = activeUsers
  })

  // collect a set of all assets transformed to data rows
  const events = []
  for (let date in eventsByDate) {
    if (!eventsByDate.hasOwnProperty(date)) continue

    if (!dateIsInRange(moment(date), dateRange)) continue

    for (let userId of eventsByDate[date]) {
      // if a user is selected, skip over the other user events
      if (selectedUser && userId !== selectedUser.id) {
        continue
      }

      // if a group filter is active, skip users not in the group
      if (groupUserIds && !groupUserIds.has(userId)) {
        continue
      }

      // assemble asset & user details and date
      const user = Object.values(allUsers).find((user) => user.id === userId)
      const event = {
        key: `${userId}-${date}`,
        name: user ? user.name : 'unknown',
        email: user ? user.email : 'unknown',
        date: date,
      }

      events.push(event)
    }
  }

  return {
    view: eventType,
    dateRange,
    bamPlatform,
    selectedUser,
    selectedGroupName,
    events,
  }
}

export const gatherAssetDataForExport = ({
  // gather all events as an array of table objects
  // primary data:
  dataObj,
  eventType, // "asset views" or "asset shares" or "user days active"
  // context data:
  allAssets, // Map of all assets in the system
  allUsers, // Map of all users
  // filters:
  bamPlatform, // Is "dam" or "mst"
  dateRange, // The date range to filter the data for.
  selectedAsset, // The selected asset id
}) => {
  // filter events by platform, dateRange, and selected asset

  // gather events for specified platform(s)
  let eventsByDate = dataObj.data[bamPlatform]
  if (bamPlatform === 'all') {
    eventsByDate = aggregatePlatformEvents(dataObj.data)
  }
  // collect a set of all assets transformed to data rows
  const events = []
  let selectedAssetTitle = undefined
  for (let date in eventsByDate) {
    if (!eventsByDate.hasOwnProperty(date)) continue

    if (!dateIsInRange(moment(date), dateRange)) continue

    for (let eventObj of eventsByDate[date]) {
      const { assetId, shareId, eventTime, userId } = eventObj
      if (!assetId || (selectedAsset && assetId !== selectedAsset)) continue

      // assemble asset & user details and date
      const asset = allAssets[assetId]
      const user = Object.values(allUsers).find((user) => user.id === userId)
      const event = {
        key: `${assetId}-${userId}-${eventTime}`,
        assetId,
        filename: asset ? asset.filename : 'unknown',
        title: asset ? asset.title || asset.filename : '(deleted asset)',
        file_type: asset ? asset.file_type : 'unknown',
        name: user ? user.name : 'unknown',
        email: user ? user.email : 'unknown',
        shareId: shareId,
        date: moment(eventTime).format('YYYY-MM-DD'),
      }

      if (selectedAsset) selectedAssetTitle = asset.title || asset.filename

      events.push(event)
    }
  }

  return {
    view: eventType,
    dateRange,
    selectedAsset: selectedAssetTitle,
    bamPlatform,
    events,
  }
}

export const gatherCalculatorDataForExport = ({
  // gather all events as an array of table objects
  // primary data:
  dataObj,
  eventType,
  // context data:
  allCalculators,
  allUsers, // Map of all users
  // filters:
  bamPlatform, // Is "dam" or "mst"
  dateRange, // The date range to filter the data for.
  selectedCalculator,
}) => {
  // filter events by platform, dateRange, and selected asset
  console.log({
    dataObj,
    eventType,
    allCalculators,
    allUsers,
    bamPlatform,
    dateRange,
    selectedCalculator,
  })

  // gather events for specified platform(s)
  let eventsByDate = dataObj.data[bamPlatform]
  if (bamPlatform === 'all') {
    eventsByDate = aggregatePlatformEvents(dataObj.data)
  }

  // collect a set of all assets transformed to data rows
  const events = []
  let selectedCalculatorTitle = undefined
  for (let date in eventsByDate) {
    if (!eventsByDate.hasOwnProperty(date)) continue

    if (!dateIsInRange(moment(date), dateRange)) continue

    for (let eventObj of eventsByDate[date]) {
      const { calcId, eventTime, userId } = eventObj
      if (!calcId || (selectedCalculator && calcId !== selectedCalculator))
        continue

      // assemble asset & user details and date
      const calcData = (allCalculators && allCalculators[calcId]) || {
        id: calcId,
        title: calcId,
      }
      const user = Object.values(allUsers).find((user) => user.id === userId)
      const event = {
        key: `${calcId}-${userId}-${eventTime}`,
        calcId,
        title: calcData ? calcData.title || calcData.id : 'unknown',
        name: user ? user.name : 'unknown',
        email: user ? user.email : 'unknown',
        date: moment(eventTime).format('YYYY-MM-DD'),
      }

      if (selectedCalculator)
        selectedCalculatorTitle = calcData
          ? calcData.title || calcId
          : 'unknown'

      events.push(event)
    }
  }

  return {
    view: eventType,
    dateRange,
    selectedCalculator: selectedCalculatorTitle,
    bamPlatform,
    events,
  }
}

export const gatherCalculationsForExport = ({
  // gather all events as an array of table objects
  // primary data:
  dataObj,
  eventType,
  // context data:
  allCalculators,
  allUsers, // Map of all users
  // filters:
  bamPlatform, // Is "dam" or "mst"
  dateRange, // The date range to filter the data for.
  selectedUser,
  calculator,
}) => {
  // filter events by platform, dateRange, and selected asset

  // gather events for specified platform(s)
  let eventsByDate = dataObj.data[bamPlatform]
  if (bamPlatform === 'all') {
    eventsByDate = aggregatePlatformEvents(dataObj.data)
  }

  // collect a set of all assets transformed to data rows
  const events = []
  let calculatorTitle = undefined
  for (let date in eventsByDate) {
    if (!eventsByDate.hasOwnProperty(date)) continue

    if (!dateIsInRange(moment(date), dateRange)) continue

    for (let eventObj of eventsByDate[date]) {
      const { calcId, eventTime, userId } = eventObj
      if (!calcId || (calculator && calcId !== calculator)) continue

      // assemble asset & user details and date
      const calcData = (allCalculators && allCalculators[calcId]) || {
        id: calcId,
        title: calcId,
      }
      const user = Object.values(allUsers).find((user) => user.id === userId)
      const event = {
        date: moment(eventTime).format('YYYY-MM-DD'),
        user: user ? user.name : 'unknown',
        email: user ? user.email : 'unknown',
        ...eventObj,
      }

      if (calculator)
        calculatorTitle = calcData ? calcData.title || calcId : 'unknown'

      events.push(event)
    }
  }

  return {
    view: eventType,
    dateRange,
    calculatorTitle,
    bamPlatform,
    selectedUser,
    events,
  }
}

/*
 * Massages the analytics api data (which comes from the redux store) so that
 * it can be displayed in the BAM Analytics Asset Table or exported report
 */
export const formatDataForAssetTable = ({
  dataObj, //  The chart data. As formatted in the redux store.
  eventType, // "views" or "shares"
  bamPlatform, // Is "dam" or "mst"
  dateRange, // The date range to filter the data for.
  selectedAsset, // The selected asset id
  selectedAssetShareData,
  selectedDate, // The selected date to filter the detail view.
  showInactive, // option to include data with no associated events
  allAssets, // Map of all assets in the system
  allUsers,
}) => {
  const showAllAssets = !selectedAsset && !selectedDate && showInactive
  const dataByPlatform = get(dataObj, 'data', null)

  // collect data by date
  let dataByDate = get(dataByPlatform, `${bamPlatform}`, null)
  if (bamPlatform === 'all') {
    dataByDate = aggregatePlatformEvents(dataByPlatform)
  }

  // collect a set of all assets transformed to data rows
  const assetToTableObj = (asset) => ({
    assetId: asset.id,
    filename: asset.filename,
    type: getLabel(asset),
    title: assetName(asset, 2000),
    link: `/asset/${asset.id}`,
    deleted: false,
  })

  // to show all assets, initialize the collection with all assets in the system
  const collectedEvents = showAllAssets
    ? Object.entries(allAssets)
        .filter(([assetId]) => !!assetId)
        .reduce((acc, [assetId, asset]) => {
          acc[assetId] = assetToTableObj(asset)
          return acc
        }, {})
    : {}

  for (let date in dataByDate) {
    if (!dataByDate.hasOwnProperty(date)) continue

    const dateMoment = moment(date)

    if (!dateIsInRange(dateMoment, dateRange)) continue
    if (!showAllAssets && selectedDate && !dateMoment.isSame(selectedDate))
      continue

    for (let eventObj of dataByDate[date]) {
      const { assetId, shareId, eventTime, userId } = eventObj

      if (!assetId || (selectedAsset && assetId !== selectedAsset)) continue

      const eventSignature = {
        eventId: shareId || 1,
        eventTime,
        userId,
      }
      const previousEvents = get(
        collectedEvents[assetId],
        `${eventType}List`,
        [],
      )

      // add the event details for this asset
      // initialize with empty values in case the asset doesn't exist in `allAssets` (e.g. it's
      // deleted)
      collectedEvents[assetId] = {
        filename: '',
        type: '',
        title: '(deleted asset)',
        link: `/asset/${assetId}`,
        deleted: true,
        ...collectedEvents[assetId],
        [`${eventType}List`]: previousEvents.concat(eventSignature),
      }

      // if collectedEvents is not pre-initialized, need to populate the asset properties
      if (!showAllAssets && allAssets[assetId]) {
        collectedEvents[assetId] = {
          ...collectedEvents[assetId],
          ...assetToTableObj(allAssets[assetId]),
        }
      }
    }
  }

  let keyedEvents = []

  if (selectedAsset) {
    const eventList = get(
      collectedEvents[selectedAsset],
      `${eventType}List`,
      [],
    )

    if (eventType === 'views') {
      // for showing asset views by user, convert the events list into a user list
      keyedEvents = Object.values(
        eventList.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.userId]: {
              ...curr,
              ...acc[curr.userId],
              events: get(acc[curr.userId], 'events', []).concat(curr),
            },
          }),
          {},
        ),
      ).map((user) => ({
        ...formatUserForDisplay(getUserById(user.userId, allUsers)),
        assetId: selectedAsset,
        key: `${eventType}ByUser_${user.userId}`,
        [eventType]: user.events.length,
      }))
    } else if (eventType === 'shares') {
      // for asset shares by user, convert the events list into a merged user-shares list
      keyedEvents = eventList.map((share) => ({
        ...(selectedAssetShareData.find(
          (data) => data.shareId === share.eventId,
        ) || {}),
        user: formatUserForDisplay(getUserById(share.userId, allUsers)).user,
        key: `${eventType}ByUser_${share.userId}_${share.eventId}`,
        shareDate: share.eventTime,
      }))
    }
  } else {
    for (let [assetId, asset] of Object.entries(collectedEvents)) {
      keyedEvents.push({
        ...asset,
        key: `allAssets_${assetId}`,
        [eventType]: get(asset, `${eventType}List`, []).length,
      })
    }
  }

  return sortBy(keyedEvents, [eventType]).reverse()
}

/*
 * Massages the analytics api data (by way of the redux store) so that
 * it can be displayed in the BAM Analytics Calculators Table or exported report
 */
export const formatDataForCalculatorsTable = ({
  dataObj, //  The chart data. As formatted in the redux store.
  eventType, // "views" or "shares"
  bamPlatform, // Is "dam" or "mst"
  dateRange, // The date range to filter the data for.
  selectedCalculator, // The selected calculator id
  selectedDate, // The selected date to filter the detail view.
  showInactive, // option to include data with no associated events
  allCalculators, // Map of all calculators in the system
  allUsers,
}) => {
  const showAllCalculators =
    !selectedCalculator && !selectedDate && showInactive
  const dataByPlatform = get(dataObj, 'data', null)

  // collect data by date
  let dataByDate = get(dataByPlatform, `${bamPlatform}`, null)
  if (bamPlatform === 'all') {
    dataByDate = aggregatePlatformEvents(dataByPlatform)
  }

  // collect a set of all calcs transformed to data rows
  const calcToTableObj = (calcId, calcData) => ({
    title: calcData.title || calcId,
    calculatorId: calcId,
  })

  // to show all calcs, initialize the collection with all assets in the system
  const collectedEvents = { ...allCalculators }

  for (let date in dataByDate) {
    if (!dataByDate.hasOwnProperty(date)) continue

    const dateMoment = moment(date)

    if (!dateIsInRange(dateMoment, dateRange)) continue
    if (!showAllCalculators && selectedDate && !dateMoment.isSame(selectedDate))
      continue

    for (let eventObj of dataByDate[date]) {
      const { calcId, eventTime, userId } = eventObj

      if (
        !calcId ||
        !calcId.length ||
        (selectedCalculator && calcId !== selectedCalculator)
      )
        continue

      const eventSignature = {
        eventId: eventTime,
        eventTime,
        userId,
      }
      const previousEvents = get(
        collectedEvents,
        `${calcId}.${eventType}List`,
        [],
      )

      // add the event details for this calculator
      // initialize with empty values in case the asset doesn't exist in `alCalculators`
      // (e.g. it's deleted)
      collectedEvents[calcId] = {
        title: calcId,
        ...(collectedEvents[calcId] || {}),
        [`${eventType}List`]: previousEvents.concat(eventSignature),
      }

      // if collectedEvents is not pre-initialized, need to populate the calc properties
      if (!showAllCalculators && allCalculators && allCalculators[calcId]) {
        collectedEvents[calcId] = {
          ...collectedEvents[calcId],
          ...calcToTableObj(calcId, allCalculators[calcId]),
        }
      }
    }
  }

  let keyedEvents = []

  if (selectedCalculator) {
    const eventList = get(
      collectedEvents[selectedCalculator],
      `${eventType}List`,
      [],
    )

    const title = collectedEvents[selectedCalculator]?.title || ''

    // for showing asset views by user, convert the events list into a user list
    keyedEvents = Object.values(
      eventList.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.userId]: {
            ...curr,
            ...acc[curr.userId],
            events: get(acc[curr.userId], 'events', []).concat(curr),
          },
        }),
        {},
      ),
    ).map((user) => {
      return {
        ...formatUserForDisplay(getUserById(user.userId, allUsers)),
        title,
        key: `${eventType}ByUser_${user.userId}`,
        [eventType]: user.events.length,
      }
    })
  } else {
    for (let [calcId, calcData] of Object.entries(collectedEvents)) {
      const calcEvents = get(calcData, `${eventType}List`, [])
      const eventCount = calcEvents.length
      if (eventCount) {
        keyedEvents.push({
          title: `${
            typeof calcData.title === 'string' ? calcData.title : calcId
          }`,
          key: `${calcId}`,
          [eventType]: eventCount,
        })
      }
    }
  }

  return sortBy(keyedEvents, [eventType]).reverse()
}

/*
 * Massages the analytics api data (by way of the redux store) so that
 * it can be displayed in the BAM Analytics Forms Table or exported report
 */
export const formatDataForFormsTable = ({
  dataObj, //  The chart data. As formatted in the redux store.
  eventType, // "submissions"
  bamPlatform, // Is "dam" or "mst"
  dateRange, // The date range to filter the data for.
  selectedForm, // The selected form id
  selectedDate, // The selected date to filter the detail view.
  showInactive, // option to include data with no associated events
  allForms, // Map of all forms in the system
  allUsers,
  formAnalyticsSettings, // Form analytics config from tenant settings (keyed by formId)
  formSubmissionsData, // Form submissions data from notifications (for status)
}) => {
  const showAllForms = !selectedForm && !selectedDate && showInactive
  const dataByPlatform = get(dataObj, 'data', null)

  // Use form analytics config from tenant settings
  const formConfigs = formAnalyticsSettings || {}

  // collect data by date
  let dataByDate = get(dataByPlatform, `${bamPlatform}`, null)
  if (bamPlatform === 'all') {
    dataByDate = aggregatePlatformEvents(dataByPlatform)
  }

  // collect a set of all forms transformed to data rows
  const formToTableObj = (formId, formData) => ({
    title: formData.title || formId,
    formId: formId,
  })

  // to show all forms, initialize the collection with all forms in the system
  const collectedEvents = { ...(allForms || {}) }
  // Also collect raw event objects for custom column display
  const rawEventsByForm = {}

  for (let date in dataByDate) {
    if (!dataByDate.hasOwnProperty(date)) continue

    const dateMoment = moment(date)

    if (!dateIsInRange(dateMoment, dateRange)) continue
    if (!showAllForms && selectedDate && !dateMoment.isSame(selectedDate))
      continue

    for (let eventObj of dataByDate[date]) {
      const { formId, eventTime, userId } = eventObj

      if (
        !formId ||
        !formId.length ||
        (selectedForm && formId !== selectedForm)
      )
        continue

      const eventSignature = {
        eventId: eventTime,
        eventTime,
        userId,
      }
      const previousEvents = get(
        collectedEvents,
        `${formId}.${eventType}List`,
        [],
      )

      // add the event details for this form
      collectedEvents[formId] = {
        title: formId,
        ...(collectedEvents[formId] || {}),
        [`${eventType}List`]: previousEvents.concat(eventSignature),
      }

      // Store raw event for custom column display
      if (!rawEventsByForm[formId]) {
        rawEventsByForm[formId] = []
      }
      rawEventsByForm[formId].push({ ...eventObj, date: dateMoment })

      // if collectedEvents is not pre-initialized, need to populate the form properties
      if (!showAllForms && allForms && allForms[formId]) {
        collectedEvents[formId] = {
          ...collectedEvents[formId],
          ...formToTableObj(formId, allForms[formId]),
        }
      }
    }
  }

  let keyedEvents = []

  if (selectedForm) {
    const formConfig = formConfigs[selectedForm] || {}
    const tableColumns = formConfig.tableColumns

    // If tableColumns are defined, show individual submissions with custom columns
    if (tableColumns && tableColumns.length > 0) {
      const rawEvents = rawEventsByForm[selectedForm] || []
      const title = collectedEvents[selectedForm]?.title || ''

      keyedEvents = rawEvents.map((eventObj, idx) => {
        // When tableColumns are defined, only include custom columns (no default fields)
        const row = {
          key: `${eventType}_${eventObj.eventTime}_${idx}`,
        }

        // Get user info for special fields
        const user = getUserById(eventObj.userId, allUsers)
        const userDisplay = formatUserForDisplayInternal(user)

        // Get submission status from formSubmissionsData
        const submissionInfo = getSubmissionStatus(
          eventObj,
          selectedForm,
          formSubmissionsData,
        )

        // Add custom columns from tableColumns config
        for (const colConfig of tableColumns) {
          const fieldName =
            typeof colConfig === 'string' ? colConfig : colConfig.field
          const label =
            typeof colConfig === 'string'
              ? colConfig
              : colConfig.label || colConfig.field

          // Check for special system fields (prefixed with underscore)
          if (fieldName === '_user') {
            row[label] = userDisplay.user
          } else if (fieldName === '_name' || fieldName === '_user_name') {
            row[label] = userDisplay.name || ''
          } else if (fieldName === '_email' || fieldName === '_user_email') {
            row[label] = userDisplay.email || ''
          } else if (fieldName === '_groups' || fieldName === '_user_groups') {
            row[label] = userDisplay.groups || ''
          } else if (fieldName === '_date') {
            row[label] = eventObj.date.format('YYYY-MM-DD')
          } else if (fieldName === '_title') {
            row[label] = title
          } else if (fieldName === '_status') {
            row[label] = submissionInfo ? submissionInfo.status : ''
          } else {
            // Find the matching in|NN|fieldName attribute
            const fieldValue = findFormFieldValue(eventObj, fieldName)
            row[label] = fieldValue !== undefined ? fieldValue : ''
          }
        }

        return row
      })
    } else {
      // Default behavior: show submissions grouped by user
      const eventList = get(
        collectedEvents[selectedForm],
        `${eventType}List`,
        [],
      )
      const rawEvents = rawEventsByForm[selectedForm] || []

      const title = collectedEvents[selectedForm]?.title || ''

      // for showing form submissions by user, convert the events list into a user list
      keyedEvents = Object.values(
        eventList.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.userId]: {
              ...curr,
              ...acc[curr.userId],
              events: get(acc[curr.userId], 'events', []).concat(curr),
            },
          }),
          {},
        ),
      ).map((user) => {
        // Find the most recent raw event for this user to get status
        const userRawEvents = rawEvents.filter((e) => e.userId === user.userId)
        const mostRecentEvent =
          userRawEvents.length > 0
            ? userRawEvents.reduce((latest, e) =>
                new Date(e.eventTime) > new Date(latest.eventTime) ? e : latest,
              )
            : null
        const submissionInfo = mostRecentEvent
          ? getSubmissionStatus(
              mostRecentEvent,
              selectedForm,
              formSubmissionsData,
            )
          : null

        return {
          ...formatUserForDisplayInternal(getUserById(user.userId, allUsers)),
          title,
          key: `${eventType}ByUser_${user.userId}`,
          [eventType]: user.events.length,
          status: submissionInfo ? submissionInfo.status : '',
        }
      })
    }
  } else {
    for (let [formId, formData] of Object.entries(collectedEvents)) {
      const formEvents = get(formData, `${eventType}List`, [])
      const eventCount = formEvents.length
      if (eventCount) {
        keyedEvents.push({
          title: `${
            typeof formData.title === 'string' ? formData.title : formId
          }`,
          key: `${formId}`,
          formId,
          [eventType]: eventCount,
        })
      }
    }
  }

  return sortBy(keyedEvents, [eventType]).reverse()
}

// Helper to find matching submission status from formSubmissionsData
// Matches by formId and eventTime (timestamp) to find the corresponding submission
function getSubmissionStatus(eventObj, formId, formSubmissionsData) {
  if (!formSubmissionsData || !Array.isArray(formSubmissionsData)) {
    return null
  }

  // Find a matching submission by formId and timestamp
  // The eventTime should match createdAt (within a small tolerance for timing differences)
  const eventTimestamp = new Date(eventObj.eventTime).getTime()

  for (const submission of formSubmissionsData) {
    if (submission.formId !== formId) continue

    const submissionTimestamp = new Date(submission.createdAt).getTime()
    // Allow 5 second tolerance for timestamp matching
    if (Math.abs(eventTimestamp - submissionTimestamp) < 5000) {
      return {
        status: submission.currentStatus,
        statusColors: submission.statusColors,
        submissionId: submission.id,
      }
    }
  }

  return null
}

// Helper to find a form field value from event attributes (handles in|NN|fieldName format)
function findFormFieldValue(eventObj, fieldName) {
  // First try direct match
  if (eventObj[fieldName] !== undefined) {
    return eventObj[fieldName]
  }
  // Look for in|NN|fieldName pattern
  for (const key of Object.keys(eventObj)) {
    if (key.startsWith('in|') && key.endsWith(`|${fieldName}`)) {
      return eventObj[key]
    }
  }
  return undefined
}

export const gatherFormDataForExport = ({
  // gather all events as an array of table objects
  // primary data:
  dataObj,
  eventType,
  // context data:
  allForms,
  allUsers, // Map of all users
  // filters:
  bamPlatform, // Is "dam" or "mst"
  dateRange, // The date range to filter the data for.
  selectedForm,
}) => {
  // gather events for specified platform(s)
  let eventsByDate = dataObj.data[bamPlatform]
  if (bamPlatform === 'all') {
    eventsByDate = aggregatePlatformEvents(dataObj.data)
  }

  // collect a set of all forms transformed to data rows
  const events = []
  let selectedFormTitle = undefined
  for (let date in eventsByDate) {
    if (!eventsByDate.hasOwnProperty(date)) continue

    if (!dateIsInRange(moment(date), dateRange)) continue

    for (let eventObj of eventsByDate[date]) {
      const { formId, eventTime, userId } = eventObj
      if (!formId || (selectedForm && formId !== selectedForm)) continue

      // assemble form & user details and date
      const formData = (allForms && allForms[formId]) || {
        id: formId,
        title: formId,
      }
      const user = Object.values(allUsers).find((user) => user.id === userId)
      const event = {
        key: `${formId}-${userId}-${eventTime}`,
        formId,
        title: formData ? formData.title || formData.id : 'unknown',
        name: user ? user.name : 'unknown',
        email: user ? user.email : 'unknown',
        date: moment(eventTime).format('YYYY-MM-DD'),
      }

      if (selectedForm)
        selectedFormTitle = formData ? formData.title || formId : 'unknown'

      events.push(event)
    }
  }

  return {
    view: eventType,
    dateRange,
    selectedForm: selectedFormTitle,
    bamPlatform,
    events,
  }
}

export const gatherFormSubmissionsForExport = ({
  // gather all events as an array of table objects
  // primary data:
  dataObj,
  eventType,
  // context data:
  allForms,
  allUsers, // Map of all users
  // filters:
  bamPlatform, // Is "dam" or "mst"
  dateRange, // The date range to filter the data for.
  selectedUser,
  form,
  formAnalyticsSettings, // Form analytics config from tenant settings (keyed by formId)
}) => {
  // Use form analytics config from tenant settings
  const formConfigs = formAnalyticsSettings || {}

  // gather events for specified platform(s)
  let eventsByDate = dataObj.data[bamPlatform]
  if (bamPlatform === 'all') {
    eventsByDate = aggregatePlatformEvents(dataObj.data)
  }

  // Get export columns config for this form (only use exportColumns, not tableColumns fallback)
  const formConfig = form ? formConfigs[form] || {} : {}
  const exportColumns = formConfig.exportColumns || null

  // collect a set of all form submissions transformed to data rows
  const events = []
  let formTitle = undefined
  for (let date in eventsByDate) {
    if (!eventsByDate.hasOwnProperty(date)) continue

    if (!dateIsInRange(moment(date), dateRange)) continue

    for (let eventObj of eventsByDate[date]) {
      const { formId, eventTime, userId } = eventObj
      if (!formId || (form && formId !== form)) continue

      // assemble form & user details and date
      const formData = (allForms && allForms[formId]) || {
        id: formId,
        title: formId,
      }
      const user = Object.values(allUsers).find((user) => user.id === userId)

      // Build event object with custom export columns if defined
      let event
      if (exportColumns && exportColumns.length > 0) {
        event = {}
        // Add custom export columns (use special _field names for system fields)
        for (const colConfig of exportColumns) {
          const fieldName =
            typeof colConfig === 'string' ? colConfig : colConfig.field
          const label =
            typeof colConfig === 'string'
              ? colConfig
              : colConfig.label || colConfig.field

          // Check for special system fields (prefixed with underscore)
          if (fieldName === '_user') {
            const userName = user ? user.name : 'unknown'
            const userEmail = user ? user.email : 'unknown'
            event[label] = userName + ' (' + userEmail + ')'
          } else if (fieldName === '_name' || fieldName === '_user_name') {
            event[label] = user ? user.name : 'unknown'
          } else if (fieldName === '_email' || fieldName === '_user_email') {
            event[label] = user ? user.email : 'unknown'
          } else if (fieldName === '_date') {
            event[label] = moment(eventTime).format('YYYY-MM-DD')
          } else if (fieldName === '_title') {
            event[label] = formData ? formData.title || formId : formId
          } else {
            const fieldValue = findFormFieldValue(eventObj, fieldName)
            event[label] = fieldValue !== undefined ? fieldValue : ''
          }
        }
      } else {
        // Default: include all fields from the event
        event = {
          date: moment(eventTime).format('YYYY-MM-DD'),
          user: user ? user.name : 'unknown',
          email: user ? user.email : 'unknown',
          ...eventObj,
        }
      }

      if (form) formTitle = formData ? formData.title || formId : 'unknown'

      events.push(event)
    }
  }

  return {
    view: eventType,
    dateRange,
    formTitle,
    bamPlatform,
    selectedUser,
    events,
  }
}

/*
 * Massages the analytics api data (by way of the redux store) so that
 * it can be displayed in the BAM Analytics Quiz Submissions Table.
 *
 * Level 1 (no quiz selected): Each row is a quiz asset with a submission count.
 * Level 2 (quiz selected): Each row is a user with submission count and passed status.
 */
export const formatDataForQuizTable = ({
  dataObj,
  bamPlatform,
  dateRange,
  selectedQuiz, // assetId of the selected quiz
  selectedDate,
  allUsers,
  allAssets,
}) => {
  const dataByPlatform = get(dataObj, 'data', null)
  let dataByDate = get(dataByPlatform, `${bamPlatform}`, null)
  if (bamPlatform === 'all') {
    dataByDate = aggregatePlatformEvents(dataByPlatform)
  }

  if (!dataByDate) return []

  const collectedEvents = {}

  for (let date in dataByDate) {
    if (!dataByDate.hasOwnProperty(date)) continue

    const dateMoment = moment(date)
    if (!dateIsInRange(dateMoment, dateRange)) continue
    if (selectedDate && !dateMoment.isSame(selectedDate)) continue

    for (let eventObj of dataByDate[date]) {
      const { assetId, eventTime, userId } = eventObj
      const effectiveId = assetId || 'unknown'
      if (selectedQuiz && effectiveId !== selectedQuiz) continue

      const eventSignature = { eventTime, userId, passed: eventObj.passed }
      const previous = get(
        collectedEvents,
        `${effectiveId}.submissionsList`,
        [],
      )
      collectedEvents[effectiveId] = {
        title: eventObj.title || effectiveId,
        ...collectedEvents[effectiveId],
        submissionsList: previous.concat(eventSignature),
      }
    }
  }

  if (selectedQuiz) {
    const eventList = get(collectedEvents[selectedQuiz], 'submissionsList', [])
    const quizAsset = allAssets && allAssets[selectedQuiz]
    const title =
      (quizAsset && (quizAsset.title || quizAsset.filename)) ||
      get(collectedEvents[selectedQuiz], 'title', selectedQuiz)

    const byUser = {}
    for (const event of eventList) {
      if (!byUser[event.userId]) {
        byUser[event.userId] = { submissions: 0, everPassed: false }
      }
      byUser[event.userId].submissions++
      if (event.passed === 'true') {
        byUser[event.userId].everPassed = true
      }
    }

    return sortBy(
      Object.entries(byUser).map(([userId, data]) => ({
        ...formatUserForDisplayInternal(getUserById(userId, allUsers)),
        title,
        key: `quizByUser_${userId}`,
        submissions: data.submissions,
        passed: data.everPassed ? 'Yes' : 'No',
      })),
      ['submissions'],
    ).reverse()
  }

  return sortBy(
    Object.entries(collectedEvents)
      .map(([key, data]) => {
        const quizAsset = allAssets && allAssets[key]
        return {
          title:
            (quizAsset && (quizAsset.title || quizAsset.filename)) ||
            data.title ||
            (key === 'unknown' ? 'Unknown Quiz' : key),
          key,
          submissions: data.submissionsList.length,
        }
      })
      .filter((row) => row.submissions > 0),
    ['submissions'],
  ).reverse()
}

/*
 * Gathers raw quiz submission events for CSV export.
 * Returns one flat object per submission with date, user, email, score, passed.
 */
export const gatherQuizSubmissionsForExport = ({
  dataObj,
  allUsers,
  allAssets,
  bamPlatform,
  dateRange,
  selectedQuiz,
  selectedDate,
}) => {
  const dataByPlatform = get(dataObj, 'data', null)
  let dataByDate = dataByPlatform && get(dataByPlatform, bamPlatform, null)
  if (bamPlatform === 'all') {
    dataByDate = aggregatePlatformEvents(dataByPlatform)
  }
  if (!dataByDate) return { events: [], dateRange, bamPlatform }

  const events = []
  for (let date in dataByDate) {
    if (!dataByDate.hasOwnProperty(date)) continue
    const dateMoment = moment(date)
    if (!dateIsInRange(dateMoment, dateRange)) continue
    if (selectedDate && !dateMoment.isSame(selectedDate)) continue

    for (let eventObj of dataByDate[date]) {
      const effectiveId = eventObj.assetId || 'unknown'
      if (selectedQuiz && effectiveId !== selectedQuiz) continue

      const asset = allAssets && allAssets[effectiveId]
      const quizName = (asset && (asset.title || asset.filename)) || effectiveId

      const user = getUserById(eventObj.userId, allUsers)
      events.push({
        quiz: quizName,
        date: moment(eventObj.eventTime).format('MM/DD/YYYY'),
        user: get(user, 'name', 'unknown'),
        email: get(user, 'email', 'unknown'),
        score: eventObj.score || '',
        passed: eventObj.passed || '',
      })
    }
  }

  return { events, dateRange, bamPlatform }
}

export const gatherGroupDataForExport = ({
  allUsers,
  allGroups,
  bamPlatform,
  dataObj,
  dateRange,
  showSystemGroups,
}) => {
  if (!allGroups) return { rows: [], dateRange, bamPlatform }

  const { userEvents, usersByEmail } = buildGroupUserEvents({
    allUsers,
    allGroups,
    bamPlatform,
    dataObj,
    dateRange,
  })

  const totalDays = Object.values(userEvents).reduce((sum, n) => sum + n, 0)

  const groupRows = []
  Object.keys(allGroups)
    .filter((groupName) => showSystemGroups || !groupName.startsWith('SYS.'))
    .forEach((groupName) => {
      const group = allGroups[groupName]
      let totalGroupDays = 0
      ;(group.members || []).forEach((email) => {
        const user = usersByEmail[email]
        if (user) totalGroupDays += userEvents[user.id] || 0
      })
      groupRows.push({
        group: group.name || groupName,
        daysActive: totalGroupDays,
      })
    })

  const rows = [
    { group: 'All Groups', daysActive: totalDays },
    ...orderBy(groupRows, ['daysActive', 'group'], ['desc', 'asc']),
  ]

  return { rows, dateRange, bamPlatform }
}

export const gatherQuizPerformanceForExport = ({
  dataObj,
  bamPlatform,
  dateRange,
  selectedQuiz,
}) => {
  const dataByPlatform = get(dataObj, 'data', null)
  let dataByDate = dataByPlatform && get(dataByPlatform, bamPlatform, null)
  if (bamPlatform === 'all') {
    dataByDate = aggregatePlatformEvents(dataByPlatform)
  }
  if (!dataByDate) return { questions: [], dateRange, bamPlatform }

  // questionMap: { [questionText]: { asked: number, correct: number } }
  const questionMap = {}

  for (let date in dataByDate) {
    if (!dataByDate.hasOwnProperty(date)) continue
    const dateMoment = moment(date)
    if (!dateIsInRange(dateMoment, dateRange)) continue

    for (let eventObj of dataByDate[date]) {
      const effectiveId = eventObj.assetId || 'unknown'
      if (selectedQuiz && effectiveId !== selectedQuiz) continue

      // Collect qr| keys (correctness per question)
      for (let key in eventObj) {
        if (!eventObj.hasOwnProperty(key)) continue
        if (!key.startsWith('qr|')) continue
        // key format: 'qr|NN|question text' (truncated to 50 chars)
        const parts = key.split('|')
        // parts[0] = 'qr', parts[1] = 'NN', parts[2+] = question text
        const questionText = parts.slice(2).join('|')
        if (!questionMap[questionText]) {
          questionMap[questionText] = { asked: 0, correct: 0 }
        }
        questionMap[questionText].asked += 1
        if (eventObj[key] === 'true') {
          questionMap[questionText].correct += 1
        }
      }
    }
  }

  const questions = Object.keys(questionMap).map(function (question) {
    const stats = questionMap[question]
    const pct =
      stats.asked > 0 ? Math.round((stats.correct / stats.asked) * 100) : 0
    return {
      question,
      asked: stats.asked,
      correct: stats.correct,
      pct: pct + '%',
    }
  })

  return { questions, dateRange, bamPlatform }
}

// Internal helper function for formatDataForFormsTable
function formatUserForDisplayInternal(user) {
  const style = {}

  if (!user || user.deleted) {
    style.color = 'lightgray'
    style.textDecoration = 'line-through'
  }

  const name = get(user, 'name', 'unknown')
  const email = get(user, 'email', 'unknown')

  return {
    user: (
      <span style={style}>
        {name} ({email})
      </span>
    ),
    userId: get(user, 'id', ''),
  }
}
